import { useMemo } from 'react'
import { formatDate } from '@luxuryescapes/lib-refunds'
import { Experiments } from 'constants/experiments'
import useExperiment from 'hooks/Experiments/useExperiment'
import type { RestrictedBedbankCancellationSplit } from 'lib/offer/cancellationPolicyUtils'

export interface RestrictedCancellationPolicyView {
  /** Customer's calendar: the window is a promise to them, measured from their purchase. */
  startDate: string
  refundToOriginalDeadline: string
  /** Same instant, unformatted, for surfaces that apply their own locale-aware formatter. */
  refundToOriginalDeadlineDate: Date
  /** Property's calendar, matching the supplier's penalty lines that follow it. */
  creditDeadline: string
  /** The upstream policy's lines after its free-cancellation sentence. */
  penaltyLines: Array<string>
}

interface UseRestrictedCancellationViewOptions {
  canActivate?: boolean
}

function useRestrictedCancellationView(
  split: RestrictedBedbankCancellationSplit | undefined,
  controlDescription: Array<string | undefined>,
  options?: UseRestrictedCancellationViewOptions,
): RestrictedCancellationPolicyView | undefined {
  const { isAnyVariant } = useExperiment(
    Experiments.sppBedbankRestrictiveCancellationPolicy,
    { canActivate: !!split && (options?.canActivate ?? true) },
  )

  return useMemo(() => {
    if (!split || !isAnyVariant) return undefined

    return {
      startDate: formatDate.formattedShortDate(split.startDate),
      refundToOriginalDeadline: formatDate.formattedShortDate(split.refundToOriginalDeadline),
      refundToOriginalDeadlineDate: split.refundToOriginalDeadline,
      creditDeadline: formatDate.formattedShortDateByTimezone(split.creditDeadline, split.timezone),
      penaltyLines: controlDescription.slice(1).filter((line): line is string => line !== undefined),
    }
  }, [split, isAnyVariant, controlDescription])
}

export default useRestrictedCancellationView
